import { t } from '@lingui/core/macro';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  deleteTriageAttachment,
  errorMessage,
  fetchTriageDocuments,
  uploadTriageAttachment,
} from '../../lib/api';
import type { TriageDocumentScope } from '../../lib/api';
import type { TriageDocuments } from '../../lib/types';

export interface PendingAttachment {
  id: string;
  file: File;
  status: 'queued' | 'uploading' | 'failed';
  error?: string;
}

/**
 * Evidence of one product assessment (version × CVE, DEV-100). The Security
 * Advisory belongs to the vulnerability case (DEV-134, see CaseAdvisoryPanel).
 */
export function useTriageDocuments(target: TriageDocumentScope) {
  // Primitive parts keep the callbacks stable across renders.
  const { versionId, cve } = target;
  const [documents, setDocuments] = useState<TriageDocuments | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState<PendingAttachment[]>([]);
  const mutationLock = useRef(false);
  const generation = useRef(0);
  const queueSequence = useRef(0);

  const load = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setLoadError(null);
    setError(null);
    try {
      const result = await fetchTriageDocuments({ versionId, cve });
      if (request !== generation.current) return;
      setDocuments(result);
    } catch (err) {
      if (request === generation.current) setLoadError(errorMessage(err));
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [versionId, cve]);

  useEffect(() => {
    void load();
    return () => {
      generation.current += 1;
    };
  }, [load]);

  async function upload(files: File[], retryId?: string): Promise<void> {
    if (!documents || mutationLock.current || files.length === 0) return;
    mutationLock.current = true;
    setBusy(true);
    setError(null);
    const items: PendingAttachment[] = files.map((file) => ({
      id: retryId ?? `upload-${++queueSequence.current}`,
      file,
      status: 'queued',
    }));
    setQueue((current) => [...current.filter((item) => item.id !== retryId), ...items]);
    let savedCount = documents.attachments.length;
    try {
      for (const item of items) {
        setQueue((current) =>
          current.map((row) => (row.id === item.id ? { ...row, status: 'uploading' } : row)),
        );
        try {
          const extension = `.${item.file.name.split('.').pop()?.toLowerCase()}`;
          if (!documents.limits.allowed_extensions.includes(extension)) {
            throw new Error(t`Dieses Dateiformat wird nicht unterstützt.`);
          }
          if (item.file.size === 0) throw new Error(t`Die Datei ist leer.`);
          if (item.file.size > documents.limits.max_file_size)
            throw new Error(t`Die Datei überschreitet die maximale Größe.`);
          if (savedCount >= documents.limits.max_files)
            throw new Error(t`Die maximale Anzahl an Anhängen ist erreicht.`);
          const attachment = await uploadTriageAttachment({ versionId, cve }, item.file);
          savedCount += 1;
          setDocuments(
            (current) =>
              current && { ...current, attachments: [...current.attachments, attachment] },
          );
          setQueue((current) => current.filter((row) => row.id !== item.id));
        } catch (err) {
          setQueue((current) =>
            current.map((row) =>
              row.id === item.id ? { ...row, status: 'failed', error: errorMessage(err) } : row,
            ),
          );
        }
      }
    } finally {
      mutationLock.current = false;
      setBusy(false);
    }
  }

  async function remove(attachmentId: string): Promise<void> {
    if (mutationLock.current) return;
    mutationLock.current = true;
    setBusy(true);
    setError(null);
    try {
      await deleteTriageAttachment({ versionId, cve }, attachmentId);
      setDocuments(
        (current) =>
          current && {
            ...current,
            attachments: current.attachments.filter((item) => item.id !== attachmentId),
          },
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      mutationLock.current = false;
      setBusy(false);
    }
  }

  return {
    scope: target,
    documents,
    loading,
    loadError,
    error,
    busy,
    queue,
    load,
    upload,
    remove,
    dismissQueued: (id: string) => setQueue((current) => current.filter((item) => item.id !== id)),
  };
}

export type TriageDocumentState = ReturnType<typeof useTriageDocuments>;
